'use client';

import { LocalizedLink } from '../LocalizedLink';
import type { NavigationNode } from '@/hooks/useNavigationMenu';

type PanelsProps = {
  activeMenu: NavigationNode | null;
  baseId: string;
  onItemClick: () => void;
  columnClass: string;
};

function NestedList({
  nodes,
  onItemClick,
  depth = 0,
}: {
  nodes: NavigationNode[];
  onItemClick: () => void;
  depth?: number;
}) {
  if (!nodes.length) return null;

  return (
    <ul
      className={`space-y-2 border-l border-white/30 pl-3 ${
        depth > 0 ? 'ml-3' : ''
      }`}
    >
      {nodes.map((child) => (
        <li key={child.id} role="none">
          <LocalizedLink
            href={child.path}
            role="menuitem"
            className="block text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            onClick={onItemClick}
          >
            {child.title}
          </LocalizedLink>

          {child.children.length > 0 && (
            <NestedList
              nodes={child.children}
              onItemClick={onItemClick}
              depth={depth + 1}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

export function RightPanels({
  activeMenu,
  baseId,
  onItemClick,
  columnClass,
}: PanelsProps) {
  return (
    <div
      id={`${baseId}-panels`}
      className={`col-span-1 gap-6 text-[18px] text-white md:col-span-2 ${columnClass} bg-[url('/images/mesh-103.png')] bg-cover bg-center p-6 rounded-br-xl`}
      aria-live="polite"
    >
      {!activeMenu && (
        <p className="text-sm">Select a menu to see its pages.</p>
      )}

      {activeMenu?.children.map((section, idx) => {
        const panelId = `${baseId}-panel-${activeMenu.id}-${idx}`;
        const hasNested = section.children.length > 0;

        return (
          <div
            key={`${activeMenu.id}-${section.id}-${idx}`}
            id={panelId}
            role="group"
            aria-labelledby={`${panelId}-label`}
            className="mb-4 break-inside-avoid"
          >
            <h4 id={`${panelId}-label`} className="mb-3 font-semibold text-white">
              {hasNested ? (
                section.title
              ) : (
                <LocalizedLink
                  href={section.path}
                  className="hover:underline"
                  onClick={onItemClick}
                >
                  {section.title}
                </LocalizedLink>
              )}
            </h4>

            {hasNested && (
              <NestedList nodes={section.children} onItemClick={onItemClick} />
            )}
          </div>
        );
      })}
    </div>
  );
}
